import React from "react";
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  Platform,
} from "react-native";
import Modal from "react-native-modal";
import { Ionicons } from "@expo/vector-icons";
import { Row, Button } from "./all";
import { normalize } from "../utils/normalize";
import { COUNTRIES } from "../constants/countries";

const JobCreationSummary = ({ 
  visible, 
  results, 
  onClose, 
  onRetry, 
  onViewJobs 
}) => {
  const getCountryInfo = (countryCode) => {
    return COUNTRIES[countryCode] || { name: countryCode, flag: '🏳️' };
  };

  const successCount = results.filter(r => r.status === 'success').length;
  const failedCount = results.filter(r => r.status === 'failed').length;
  const hasFailures = failedCount > 0;
  const allFailed = failedCount === results.length;

  const getHeaderIcon = () => {
    if (allFailed) {
      return (
        <View
          style={{
            width: 60,
            height: 60,
            borderRadius: 30,
            backgroundColor: "#FFE5E5",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Ionicons name="close-circle-outline" size={32} color="#FF4B4B" />
        </View>
      );
    } else if (hasFailures) {
      return (
        <View
          style={{
            width: 60,
            height: 60,
            borderRadius: 30,
            backgroundColor: "#FFF4E5",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Ionicons name="warning-outline" size={32} color="#FF9800" />
        </View>
      );
    } else {
      return (
        <View
          style={{
            width: 60,
            height: 60,
            borderRadius: 30,
            backgroundColor: "#E8F5E9",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Ionicons name="checkmark-circle-outline" size={32} color="#4CAF50" />
        </View>
      );
    }
  };

  const getHeaderTitle = () => {
    if (allFailed) {
      return "Job Creation Failed";
    } else if (hasFailures) {
      return "Partially Completed";
    } else {
      return "Jobs Created Successfully";
    }
  };

  const getHeaderMessage = () => {
    if (allFailed) {
      return "Unable to create job postings. Please try again.";
    } else if (hasFailures) {
      return `Successfully created ${successCount} job${successCount > 1 ? 's' : ''}, ${failedCount} failed`;
    } else {
      return `All ${successCount} job${successCount > 1 ? 's' : ''} created successfully`;
    }
  };

  const failedCountries = results.filter(r => r.status === 'failed').map(r => r.country);

  return (
    <Modal
      isVisible={visible}
      onBackdropPress={onClose}
      animationType="fade"
      backdropOpacity={0.7}
      useNativeDriverForBackdrop={true}
      style={{
        margin: 0,
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <View
        style={{
          backgroundColor: "white",
          borderRadius: 16,
          width: Platform.OS === "web" ? 420 : "90%",
          maxWidth: 420,
          maxHeight: "80%",
          shadowColor: "#000",
          shadowOffset: {
            width: 0,
            height: 2,
          },
          shadowOpacity: 0.25,
          shadowRadius: 3.84,
          elevation: 5,
        }}
      >
        {/* Header */}
        <View style={{ padding: 24, alignItems: "center" }}>
          {getHeaderIcon()}
          <Text
            style={{
              fontSize: normalize(18),
              fontWeight: "600",
              color: "#333",
              marginTop: 16,
              marginBottom: 4,
            }}
          >
            {getHeaderTitle()}
          </Text>
          <Text
            style={{
              fontSize: normalize(14),
              color: "#666",
              textAlign: "center",
            }}
          >
            {getHeaderMessage()}
          </Text>
        </View>

        {/* Results List */}
        <ScrollView
          style={{
            maxHeight: 300,
            borderTopWidth: 1,
            borderBottomWidth: 1,
            borderColor: "#F0F0F0",
          }}
        >
          <View style={{ paddingHorizontal: 24, paddingVertical: 12 }}>
            {results.map((result, index) => {
              const countryInfo = getCountryInfo(result.country);
              const isSuccess = result.status === 'success';
              
              return (
                <Row
                  key={result.country}
                  style={{
                    paddingVertical: 12,
                    borderBottomWidth: index < results.length - 1 ? 1 : 0,
                    borderBottomColor: "#F0F0F0",
                  }}
                >
                  <Text style={{ fontSize: normalize(18), marginRight: 12 }}>
                    {countryInfo.flag}
                  </Text>
                  <View style={{ flex: 1 }}>
                    <Text
                      style={{
                        fontSize: normalize(15),
                        color: "#333",
                        fontWeight: "500",
                      }}
                    >
                      {countryInfo.name}
                    </Text>
                    {isSuccess ? (
                      <Text
                        style={{
                          fontSize: normalize(12),
                          color: "#4CAF50",
                          marginTop: 2,
                        }}
                      >
                        Job created successfully
                      </Text>
                    ) : (
                      <Text
                        style={{
                          fontSize: normalize(12),
                          color: "#FF4B4B",
                          marginTop: 2,
                        }}
                      >
                        {result.error || "Failed to create job"}
                      </Text>
                    )}
                  </View>
                  {isSuccess ? (
                    <Ionicons name="checkmark-circle" size={24} color="#4CAF50" />
                  ) : (
                    <Ionicons name="close-circle" size={24} color="#FF4B4B" />
                  )}
                </Row>
              );
            })}
          </View>
        </ScrollView>

        {/* Actions */}
        <View style={{ padding: 24 }}>
          {hasFailures && !allFailed && (
            <Button
              label={`Retry Failed Countries (${failedCount})`}
              onPress={() => onRetry(failedCountries)}
              type="primary"
              style={{ marginBottom: 12 }}
            />
          )}
          
          {!allFailed && (
            <Button
              label="View Created Jobs"
              onPress={onViewJobs}
              type={hasFailures ? "secondary" : "primary"}
              style={{ marginBottom: 12, alignItems: "center" }}
            />
          )}
          
          <TouchableOpacity
            onPress={onClose}
            style={{
              paddingVertical: 12,
              alignItems: "center",
            }}
          >
            <Text
              style={{
                fontSize: normalize(15),
                color: "#666",
                fontWeight: "500",
              }}
            >
              {allFailed ? "Close" : "Done"}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

export default JobCreationSummary;